import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicingState, Order } from 'models/order';
import FilesImagesUploadAreaAndTable from 'components/shared/files-images-upload-area-and-table/files-images-upload-area-and-table.component';
import DrawImageDialog from 'components/shared/draw-image-dialog/draw-image-dialog.component';
import { File as DenthubFile } from 'models/file';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { deleteFileTemplate } from 'apollo/templates/files';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { Box, Button } from '@mui/material';
import { imageAnnotate } from 'utils/image-annotate';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import axios, { AxiosProgressEvent, CancelTokenSource } from 'axios';
import LinearProgressBarWithLabel from 'components/shared/linear-progress-bar-with-label/linear-progress-bar-with-label.component';
import Cookies from 'js-cookie';
import { OrderDetailsContext } from 'pages/order-details/order-details-context';
import { useRefreshOrder } from 'hooks/useRefreshOrder';
import { useUploadFailureHandler } from 'hooks/useUploadFailureHandler';
import { startUploadAttempt } from 'utils/upload-failure';

type FilesImagesSectionProps = {
    versionCheck: boolean;
    order: Order;
};
const FilesImagesSection: FC<FilesImagesSectionProps> = ({ versionCheck, order }: FilesImagesSectionProps) => {
    const { t: translate } = useTranslation(['orders', 'order-event']);
    const { refetch } = useContext(OrderDetailsContext);
    const refreshOrder = useRefreshOrder();
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const [files, setFiles] = useState<DenthubFile[]>([]);
    const [filesSnackbar, setFilesSnackbar] = useState(false);
    const [deleteFileSnackbar, setDeleteFileSnackbar] = useState(false);
    const [openDrawImage, setOpenDrawImage] = useState(false);
    const [dentistAddScanDialogOpen, setDentistAddScanDialogOpen] = useState(false);
    const [progress, setProgress] = useState<number>(0);
    const [_, setCancelToken] = useState<CancelTokenSource | null>(null);
    const [uploading, setUploading] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const handleUploadFailure = useUploadFailureHandler();
    const is3ShapeEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.THREE_SHAPE_INTEGRATION,
    );

    const [deleteFile, { loading: fileDeleteLoading }] = useMutation(deleteFileTemplate, {
        onCompleted: ({ deleteFile }) => {
            const filesData = files.filter((item) => item._id !== deleteFile);
            setFiles(filesData);
            // deleteFile returns only the file id: re-read the order (files, new version)
            // rather than guessing it here (DD-3231).
            refreshOrder(order._id);
            setDeleteFileSnackbar(true);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setFiles(order.filesUrl);
    }, [order.filesUrl]);

    const uploadOrderFiles = async (files: File[]) => {
        const attempt = startUploadAttempt(files);
        try {
            setUploading(true);
            setFilesSnackbar(false);
            const source = axios.CancelToken.source();
            setCancelToken(source);
            const formData = new FormData();
            for (const file of files) {
                formData.append('files', file, file.name);
            }
            const response = await axios.post(`${CONSTANTS.DOC_API_URI}/upload/orders/${order._id}/files`, formData, {
                headers: {
                    Authorization: Cookies.get(CONSTANTS.TOKEN_KEY),
                },
                onUploadProgress: (ev: AxiosProgressEvent) => {
                    attempt.sentBytes = ev.loaded;
                    setProgress((ev.loaded / (ev.total || 100)) * 100);
                },
                cancelToken: source.token,
            });
            if (response.status === 200 && response.data) {
                // The upload goes over REST, which Apollo never sees: re-read the order.
                refreshOrder(order._id);
                setFilesSnackbar(true);
            }
        } catch (error) {
            handleUploadFailure(error, { upload: 'order-files', attempt, retry: () => uploadOrderFiles(files) });
        } finally {
            setProgress(0);
            setUploading(false);
        }
    };

    const handleChange = async (files: File[]) => {
        if (!files || files.length === 0) {
            return;
        }
        await uploadOrderFiles(files);
    };

    const handleDeleteFile = async (fileId: string) => {
        if (fileId) {
            setDeleteFileSnackbar(false);
            await deleteFile({ variables: { _id: fileId, orderId: order._id } });
        }
    };
    const handleDrawImageClick = () => {
        setOpenDrawImage(true);
    };

    const blobToFile = (theBlob: Blob, fileName: string) => {
        return new File([theBlob], fileName, { lastModified: new Date().getTime(), type: theBlob.type });
    };

    const imageAnnotateUploadingProcess = async (elementId: string, filename: string) => {
        imageAnnotate.showMarkerArea(
            elementId,
            translate('orders:CONFIRM_CLOSE'),
            (dataUrl) => {
                setOpenDrawImage(false);
                if (dataUrl) {
                    fetch(dataUrl)
                        .then((res) => res.blob())
                        .then(async (fileBlob) => {
                            const fileObj = blobToFile(fileBlob, filename);
                            await uploadOrderFiles([fileObj]);
                        });
                }
            },
            () => setOpenDrawImage(false),
        );
    };

    const handleAddImage = async (elementId: string, filename: string) => {
        await imageAnnotateUploadingProcess(elementId, filename);
    };

    const handleEditAndUploadImage = async (file: DenthubFile) => {
        await imageAnnotateUploadingProcess(file._id, file.filename);
    };

    const modifyLocked = () => {
        return order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY;
    };

    const handleAttachIntraOralScanClick = () => {
        setDentistAddScanDialogOpen(true);
    };

    return (
        <Fragment>
            {filesSnackbar ? (
                <AlertSnackbar type="success" message={translate('order-event:SUCCESS_FILES')} />
            ) : undefined}
            {deleteFileSnackbar ? (
                <AlertSnackbar type="success" message={translate('order-event:SUCCESS_DELETE_FILE')} />
            ) : undefined}
            {versionCheck && !modifyLocked() && !permissions.isExternalTechnician(currentUser.role) ? (
                <Box>
                    <Button onClick={handleDrawImageClick} data-test="files-images-section.draw-image-button">
                        {translate('orders:DRAW_NEW_IMAGE')}
                    </Button>
                </Box>
            ) : undefined}
            <FilesImagesUploadAreaAndTable
                files={files}
                onDeleteFile={handleDeleteFile}
                onFileChange={handleChange}
                onEditFile={handleEditAndUploadImage}
                versionCheck={versionCheck && !modifyLocked() && !permissions.isExternalTechnician(currentUser.role)}
                showHightLight={true}
                originalOrder={order.originalOrder}
            />
            {uploading ? <LinearProgressBarWithLabel progress={progress} /> : undefined}
            {fileDeleteLoading ? <Loader /> : undefined}

            <DrawImageDialog
                onAddImage={handleAddImage}
                open={openDrawImage}
                onClose={() => {
                    setOpenDrawImage(false);
                }}
            />
        </Fragment>
    );
};

export default FilesImagesSection;
